import axios from 'axios'

import type {
    GetInvoicesListArgs,
    GetInvoicesListData,
    GetPaymentsArgs,
    GetPaymentsData,
    GetTenantData,
    GetTenantsArgs,
    GetTenantsData,
    UpdatePaymentRequest,
    UpdateAutopayRequest,
    GetAutopayAgreementData,
    GetTenantSearchArgs,
    GetTenantSearch,
    InvoiceSummaries,
    Note,
    PaymentSummaries,
    FilesResult,
    Tag,
    LastModifiedObject,
    Opportunity,
    GetFilesArgs,
    Utility,
    AccountManager,
    GetProgramsArgs,
    GetProgramsListData,
    DetailedProgramItem,
    AllocationReadinessResult,
    AllocationSubmission,
    UtilityPortalCredentials,
    LinkUtilityPortalResult,
    ReadinessChecklist,
    UpdateScraperIsActiveResult,
    BillingOptionsResponse,
    UpdateUtilityBillingConfigBody,
    SharedTenantAccountUpdateResponse,
    UpdateKingBillingConfigBody,
    CreateKingIncentivePlanBody,
    CreateUtilityIncentivePlanBody,
    IncentivePlanOptionsResponse,
    IncentivePlanAuditEntry,
    BillingSdkTenantAccountBalances,
    Paginated,
    NoteSearchParams,
    UpdateProgramBody,
    CreateProgramBody,
    CreateProgramResponse,
    TenantAccountBillingEventEntry,
    GetDocumentsArgs,
    GetDocumentsData,
    DocumentMetadata,
    TenantAccountBillingStatusResponse,
    TenantAccountStatusResponse,
    CcFeePolicyAdmin,
    EditBillingEventRequest,
    ReverseInvoiceAccruedRequest,
    BillingEventEnums,
    GetVNEMAllocationsResponse,
    ActivateVNEMAllocationResponse,
    UpdateVNEMPercentagesBody,
    UpdateVNEMPercentagesResponse,
    BillingEventAuditLogEntry,
    EventReportEntry,
    EventSummary,
    GetEventReportArgs,
    UpdateAuditLogStatusRequest,
    UpdateAuditLogEventBodyRequest,
    SmdJobResponse,
    TenantTicket,
    TicketSearchParams,
    TenantCallsSearchParams,
    TicketEnums,
    IntervalDataReadinessChecklistItem,
    UtilityType,
    OptiRateTargetTariff,
    CreditPayee,
    StatementType,
    ReconciliationReportVO,
    ReconciliationQueryParams,
    GoLiveReadinessVO,
    GetTariffsArgs,
    GetTariffsListData,
    DetailedTariffItem,
    UpdateTariffBody,
    UpdateTariffRateBody,
    CreateTariffBody,
    CreateTariffRateBody,
    CreateTariffHistoryBody,
    TariffActivatePreview,
    TariffHistoryReasonBody,
    TariffRateItem,
    TariffHistoryItem,
    TariffHistoryAttachment,
    TariffRateSheetCategory,
    GetTariffUsagesData,
    TariffAuditEntityType,
    GetTariffAuditHistoryData,
    TariffRateAnalysisRow,
    AccRateVintage,
    AccRateGridRow,
    CreateUtilityBody,
    UpdateUtilityBody,
    CreateProgramGeneratingMeterBody,
    GeneratingMeterOption,
    ProgramGeneratingMeterRow,
    RetireProgramGeneratingMeterBody,
    UpdateProgramGeneratingMeterBody,
    CreateProgramMeterBody,
    ProgramMeterOption,
    ProgramMeterRow,
    RetireProgramMeterBody,
    UpdateProgramMeterBody,
    LinkPropertyUtilityBody,
    CreateEnergyTaxBody,
    EnergyTaxCatalogItem,
    PropertyUtilityRow,
    PropertyUtilityTaxRow,
    SyncPropertyUtilityTaxesBody,
    SyncPropertyUtilityTaxesResult,
    UtilityOption,
} from './types'

const BASE_URL = '/api/v1/admin'
const accessToken = localStorage.getItem('accessToken')
axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`

export const getTenantsApi = async (args: GetTenantsArgs): Promise<GetTenantsData> => {
    return await axios.get(`${BASE_URL}/tenant`, { params: args }).then((res) => res.data as GetTenantsData)
}

export const getTenantSearchApi = async (query: GetTenantSearchArgs): Promise<GetTenantSearch> => {
    // eslint-disable-next-line  @typescript-eslint/no-unsafe-return
    return await axios.get(`${BASE_URL}/tenant/search`, { params: query }).then((res) => res?.data as GetTenantSearch)
}

export const getTenantListApi = async (): Promise<
    Array<{
        id: string
        name: string
    }>
> => {
    return await axios.get(`${BASE_URL}/tenant/list`).then(
        (res) =>
            res.data as Array<{
                id: string
                name: string
            }>
    )
}

export const getTenantApi = async (tenantId: string): Promise<GetTenantData> => {
    return await axios.get(`${BASE_URL}/tenant/${tenantId}`).then((res) => res.data as GetTenantData)
}

export const updateTenantApi = async (tenantId: string, payload: unknown): Promise<unknown> => {
    return await axios.put(`${BASE_URL}/tenant/${tenantId}`, payload).then((res) => res.data as unknown)
}

export const updateTenantAccountNameApi = async (
    tenantAccountId: string,
    accountName: string
): Promise<SharedTenantAccountUpdateResponse> => {
    return await axios
        .put(`${BASE_URL}/tenant-account/${tenantAccountId}/name`, { accountName })
        .then((res) => res.data as SharedTenantAccountUpdateResponse)
}

export const updateMeterServiceApi = async (
    meterServiceId: string,
    payload: MeterServiceUpdatePayload
): Promise<unknown> => {
    return await axios.put(`${BASE_URL}/tenant/meter-service/${meterServiceId}`, payload).then((res) => res.data as unknown)
}

export interface MeterServiceTariffOption {
    schedule: string
    name: string | null
    utilityShortName?: string | null
    utilityName?: string | null
}

export interface MeterServiceTariffOptions {
    serviceTariffs: MeterServiceTariffOption[]
    ccaApplies: boolean
    ccaUtilityShortName: string | null
    ccaTariffs: MeterServiceTariffOption[]
}

export interface MeterServiceUpdatePayload {
    startDate?: string | null
    endDate?: string | null
    active?: boolean
    salesforceId?: string | null
    serviceAgreementId?: string
    tariffSchedule?: string
    ccaTariffSchedule?: string | null
    ccaTariffUtilityShortName?: string
    tariffEffectiveDate?: string | null
    tariffEndDate?: string | null
    ccaTariffEffectiveDate?: string | null
    ccaTariffEndDate?: string | null
    ccaVintageYear?: string | null
    meterAccountType?: 'BENEFITTING' | 'GENERATOR'
    postBillingTransition?: boolean
}

export interface MeterServiceCreatePayload {
    serviceAgreementId: string
    meterNumber: string
    startDate: string
    tariffSchedule: string
    meterAccountType: 'BENEFITTING' | 'GENERATOR'
    endDate?: string
    active?: boolean
    postBillingTransition?: boolean
    salesforceId?: string
    serviceAddress?: {
        street1: string
        street2?: string
        city: string
        state: string
        zip: string
    }
    tariffEffectiveDate?: string | null
    tariffEndDate?: string | null
    ccaTariffSchedule?: string | null
    ccaTariffUtilityShortName?: string
    ccaTariffEffectiveDate?: string | null
    ccaTariffEndDate?: string | null
    ccaVintageYear?: string | null
}

export const getMeterServiceTariffOptionsApi = async (
    meterServiceId: string
): Promise<MeterServiceTariffOptions> => {
    return await axios
        .get(`${BASE_URL}/tenant/meter-service/${meterServiceId}/tariff-options`)
        .then((res) => res.data as MeterServiceTariffOptions)
}

export const getTenantAccountTariffOptionsApi = async (
    tenantAccountId: string
): Promise<MeterServiceTariffOptions> => {
    return await axios
        .get(`${BASE_URL}/tenant/account/${tenantAccountId}/tariff-options`)
        .then((res) => res.data as MeterServiceTariffOptions)
}

export const createMeterServiceApi = async (
    tenantAccountId: string,
    payload: MeterServiceCreatePayload
): Promise<{ cuid: string }> => {
    return await axios
        .post(`${BASE_URL}/tenant/account/${tenantAccountId}/meter-service`, payload)
        .then((res) => res.data as { cuid: string })
}

export const updateContactApi = async (tenantId: string, contactId: string, payload: unknown): Promise<unknown> => {
    return await axios.put(`${BASE_URL}/tenant/${tenantId}/contact/${contactId}`, payload).then((res) => res.data as unknown)
}

export const addPaymentApi = async (payload: unknown): Promise<unknown> => {
    return await axios.post(`${BASE_URL}/payment`, payload).then((res) => res.data as unknown)
}

export const addOneTimePaymentMethodApi = async (payload: unknown): Promise<{ paymentMethodId: string }> => {
    return await axios.post(`${BASE_URL}/payment/paymentMethod/oneTime`, payload).then((res) => res.data as { paymentMethodId: string })
}

export const makeOneTimePaymentApi = async (payload: unknown): Promise<any> => {
    return await axios.post(`${BASE_URL}/payment/oneTime`, payload).then((res) => res.data as any)
}

export const postUtilityPaymentApi = async (payload: unknown): Promise<any> => {
    return await axios.post(`${BASE_URL}/payment/utility`, payload).then((res) => res.data as any)
}

export const postCorpPaymentApi = async (payload: unknown): Promise<any> => {
    return await axios.post(`${BASE_URL}/payment/corp`, payload).then((res) => res.data as any)
}

export const postUtilityDepositPaymentApi = async (payload: unknown): Promise<any> => {
    return await axios.post(`${BASE_URL}/payment/utility/deposit`, payload).then((res) => res.data as any)
}

export const postManualUtilityPaymentApi = async (payload: unknown): Promise<any> => {
    return await axios.post(`${BASE_URL}/payment/manual-utility`, payload).then((res) => res.data as any)
}

export const updatePaymentApi = async (payload: UpdatePaymentRequest): Promise<unknown> => {
    return await axios.patch(`${BASE_URL}/payment`, payload).then((res) => res.data as unknown)
}

export const updateAutopayApi = async (payload: UpdateAutopayRequest, file?: File): Promise<void> => {
    if (file == null) {
        await axios.patch(`${BASE_URL}/payment/autopay`, payload)
        return
    }

    const formData = new FormData()
    formData.append('tenantAccountId', payload.tenantAccountId)
    formData.append('userId', payload.userId)
    formData.append('enabled', payload.enabled ? 'true' : 'false')
    if (payload.paymentMethodId != null && payload.paymentMethodId !== '') {
        formData.append('paymentMethodId', payload.paymentMethodId)
    }
    formData.append('file', file)
    await axios.patch(`${BASE_URL}/payment/autopay`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    })
}

export const getAutopayAgreementApi = async (tenantAccountId: string): Promise<GetAutopayAgreementData> => {
    return await axios
        .get(`${BASE_URL}/payment/autopay/agreement`, { params: { tenantAccountId } })
        .then((res) => res.data as GetAutopayAgreementData)
}

export const uploadAutopayAgreementApi = async (tenantAccountId: string, file: File): Promise<void> => {
    const formData = new FormData()
    formData.append('tenantAccountId', tenantAccountId)
    formData.append('file', file)
    await axios.post(`${BASE_URL}/payment/autopay/agreement`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    })
}

export const impersonateApi = async (
    accountManagerCuid: string
): Promise<{
    impersonationToken: string
    tokenExpirationSeconds: number
}> => {
    return await axios.post(`${BASE_URL}/auth/user/${accountManagerCuid}/impersonate`).then(
        (res) =>
            res.data as {
                impersonationToken: string
                tokenExpirationSeconds: number
            }
    )
}

export const impersonateForOneTimePaymentApi = async (
    accountManagerCuid: string
): Promise<{
    impersonationToken: string
    tokenExpirationSeconds: number
}> => {
    return await axios.post(`${BASE_URL}/auth/user/${accountManagerCuid}/impersonate/oneTimePayment`).then(
        (res) =>
            res.data as {
                impersonationToken: string
                tokenExpirationSeconds: number
            }
    )
}

export const getPropertyListApi = async (): Promise<
    Array<{
        id: string
        name: string
    }>
> => {
    return await axios.get(`${BASE_URL}/property`).then(
        (res) =>
            res.data as Array<{
                id: string
                name: string
            }>
    )
}

export const getUtilityListApi = async (): Promise<Utility[]> => {
    return await axios.get(`${BASE_URL}/utility`).then((res) => res.data as Utility[])
}

export const getAdminUtilitiesApi = async (type?: string): Promise<Utility[]> => {
    return await axios
        .get(`${BASE_URL}/utility`, { params: { liveProjects: false, ...(type != null && { type }) } })
        .then((res) => res.data as Utility[])
}

export interface UtilityTariffOption {
    schedule: string
    name: string | null
    utilityShortName: string | null
}

export const getUtilityTariffsApi = async (): Promise<UtilityTariffOption[]> => {
    return await axios.get(`${BASE_URL}/utility/tariffs`).then((res) => res.data as UtilityTariffOption[])
}

export const generateAccountInvitationApi = async (
    inviteIds: string[],
    options?: { forceCreateDespiteConflicts?: boolean },
): Promise<unknown> => {
    return await axios
        .post(`${BASE_URL}/tenant/seed/invites`, inviteIds, {
            params: options?.forceCreateDespiteConflicts ? { forceCreateDespiteConflicts: true } : undefined,
        })
        .then((res) => res.data as unknown)
}

export const getInvoiceSummaryApi = async (): Promise<InvoiceSummaries> => {
    return await axios.get(`${BASE_URL}/collections/invoice/summary`).then((res) => res.data as InvoiceSummaries)
}

export const updateInvoiceApi = async (invoiceId: string, payload: unknown): Promise<unknown> => {
    return await axios.patch(`${BASE_URL}/invoice/${invoiceId}`, payload).then((res) => res.data as unknown)
}

export const getStatementApi = async (invoiceId: string, type: StatementType): Promise<Blob> => {
    return await axios
        .get(`${BASE_URL}/collections/invoice/${invoiceId}/statement`, {
            params: { type: type },
            responseType: 'arraybuffer',
        })
        .then((response) => response.data as Blob)
}

export const bulkDownloadInvoicesApi = async (invoiceCuids: string[], statementType: StatementType): Promise<Blob> => {
    return await axios
        .post(`${BASE_URL}/collections/invoice/bulk-download`, { invoiceCuids, statementType }, { responseType: 'blob' })
        .then((res) => res.data as Blob)
}

export const getInvoiceListApi = async (query: GetInvoicesListArgs): Promise<GetInvoicesListData> => {
    return await axios.get(`${BASE_URL}/collections/invoice`, { params: query }).then((res) => res.data as GetInvoicesListData)
}

export const getInvoiceListBatchApi = async (
  tenantAccountIds: string[],
  resultsPerPage: number = 5,
  options?: { includeSummary?: boolean }
): Promise<Record<string, GetInvoicesListData>> => {
    const params = new URLSearchParams()
    params.append('tenantAccountIds', tenantAccountIds.join(','))
    params.append('resultsPerPage', resultsPerPage.toString())
    if (options?.includeSummary === false) {
      params.append('includeSummary', 'false')
    }
    return await axios.get(`${BASE_URL}/collections/invoice/batch?${params.toString()}`).then((res) => res.data as Record<string, GetInvoicesListData>)
}

export const getPaymentSummaryApi = async (): Promise<PaymentSummaries> => {
    return await axios.get(`${BASE_URL}/collections/payment/summary`).then((res) => res.data as PaymentSummaries)
}

export const getPaymentListApi = async (query: GetPaymentsArgs): Promise<GetPaymentsData> => {
    return await axios.get(`${BASE_URL}/collections/payment`, { params: query }).then((res) => res.data as GetPaymentsData)
}

export const updateTenantPreferenceApi = async (payload: unknown): Promise<unknown> => {
    return await axios.patch(`${BASE_URL}/tenant/preference`, payload).then((res) => res.data as unknown)
}

export const getNotesApi = async (query: NoteSearchParams): Promise<Paginated<Note>> => {
    return await axios.get(`${BASE_URL}/note`, { params: query }).then((response) => response.data as Paginated<Note>)
}

export const addNoteApi = async (payload: { sourceEntity: string; body: string }): Promise<Note> => {
    return await axios.post(`${BASE_URL}/note`, { sourceEntity: payload.sourceEntity, body: payload.body }).then((response) => response.data as Note)
}

export const updateNoteApi = async (payload: { noteCuid: string; body?: string; isPinned?: boolean; tags?: string[] }): Promise<Note> => {
    return await axios
        .put(`${BASE_URL}/note/${payload.noteCuid}`, { body: payload.body, isPinned: payload.isPinned, tags: payload.tags })
        .then((response) => response.data as Note)
}

export const deleteNoteApi = async (payload: { noteCuid: string }): Promise<Note> => {
    return await axios.delete(`${BASE_URL}/note/${payload.noteCuid}`).then((response) => response.data as Note)
}

export const getTenantCallsApi = async (
    tenantCuid: string,
    query?: TenantCallsSearchParams
): Promise<Paginated<Note>> => {
    return await axios
        .get(`${BASE_URL}/tenant/${tenantCuid}/calls`, { params: query })
        .then((response) => response.data as Paginated<Note>)
}

export const postAccountManagerInviteApi = async (payload: { tenantAccountId: string; emailAddress: string }): Promise<AccountManager> => {
    return await axios.post(`${BASE_URL}/tenant-account/account-manager/create`, payload).then((response) => response.data as AccountManager)
}

export const disableAccountManagerApi = async (payload: { tenantAccountId: string; emailAddress: string }): Promise<unknown> => {
    return await axios.put(`${BASE_URL}/tenant-account/account-manager/disable`, payload).then((response) => response.data as unknown)
}

export const resetPasswordAccountManagerApi = async (payload: { tenantAccountId: string; emailAddress: string }): Promise<{ passwordResetLink: string }> => {
    return await axios.post(`${BASE_URL}/tenant-account/account-manager/reset-password`, payload).then((response) => response.data as { passwordResetLink: string })
}

export const generatePasswordResetLinkAccountManagerApi = async (
    payload: { tenantAccountId: string; emailAddress: string }
): Promise<{ passwordResetLink: string; expiresInDays: number }> => {
    return await axios
        .post(`${BASE_URL}/tenant-account/account-manager/password-reset-link`, payload)
        .then((response) => response.data as { passwordResetLink: string; expiresInDays: number })
}

export const getFileListApi = async (query: GetFilesArgs): Promise<FilesResult> => {
    return await axios.get(`${BASE_URL}/file-explorer/files`, { params: query }).then((response) => response.data as FilesResult)
}

export const getFileExtensionsListApi = async (): Promise<string[]> => {
    return await axios.get(`${BASE_URL}/file-explorer/file-extensions`).then((res) => res.data as string[])
}

export const getLastModifiedApi = async (): Promise<LastModifiedObject> => {
    return await axios.get(`${BASE_URL}/file-explorer/files/modified-by`).then((res) => res.data as LastModifiedObject)
}

export const getOpportunitiesApi = async (): Promise<Opportunity> => {
    return await axios.get(`${BASE_URL}/file-explorer/opportunities`).then((res) => res.data as Opportunity)
}

export const getTagListApi = async (): Promise<Tag[]> => {
    return await axios.get(`${BASE_URL}/file-explorer/tags`).then((res) => res.data as Tag[])
}

// Commented out until duplicate directories in GDrive are figured out
// export const getDirectoriesApi = async (opportunityId: string | null): Promise<DirectoryObject> => {
//   return await axios.get(`${BASE_URL}/file-explorer/directories`, { params: opportunityId }).then((res) => res.data as DirectoryObject)
// }

export const getOpportunityFolderIdApi = async (opportunityId: string): Promise<string> => {
    return await axios
        .get(`${BASE_URL}/file-explorer/opportunityFolder`, {
            params: { opportunityId },
        })
        .then((res) => res.data as string)
}

export const deleteTagForFileApi = async (fileIds: string[]): Promise<unknown> => {
    return await axios.delete(`${BASE_URL}/file-explorer/tags`, { data: { fileIds: fileIds } }).then((res) => res.data as unknown)
}

export const addTagToFileApi = async (fileIds: string[], tag: Tag): Promise<unknown> => {
    return await axios.put(`${BASE_URL}/file-explorer/tags`, { fileIds, tag }).then((res) => res.data as unknown)
}

export const renameFileApi = async (fileId: string, newName: string): Promise<unknown> => {
    return await axios.patch(`${BASE_URL}/file-explorer/files/rename`, { fileId, newName }).then((res) => res.data as unknown)
}

export const downloadFileApi = async (fileIds: string[]): Promise<Blob> => {
    return await axios.post(`${BASE_URL}/file-explorer/files/download`, { fileIds }, { responseType: 'blob' }).then((res) => res.data as Blob)
}

export const downloadPGEDOBWithFilenameApi = async (
    tenantAccountId: string,
    startDate?: string,
    endDate?: string
): Promise<{ data: Blob; filename: string }> => {
    const body: { startDate?: string; endDate?: string } = {}
    if (startDate) body.startDate = startDate
    if (endDate) body.endDate = endDate

    const response = await axios.post(`${BASE_URL}/tenant/account/${tenantAccountId}/pge-dob/download`, body, { responseType: 'blob' })

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers['content-disposition']
    let filename = 'pge-dob-download'

    if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/)
        if (filenameMatch) {
            filename = filenameMatch[1]
        }
    }

    return {
        data: response.data as Blob,
        filename,
    }
}

export const checkDOBAvailabilityBulkApi = async (
    tenantAccountId: string,
    dateRanges: Array<{ billingStart: string; billingEnd: string }>
): Promise<Record<string, boolean>> => {
    return await axios
        .post(`${BASE_URL}/tenant/account/${tenantAccountId}/pge-dob/bulk-availability-check`, { dateRanges })
        .then((res) => res.data as Record<string, boolean>)
}
export const getProgramsApi = async (query: GetProgramsArgs): Promise<GetProgramsListData> => {
    return await axios.get(`${BASE_URL}/programs`, { params: query }).then((response) => response.data as GetProgramsListData)
}

export const getProgramApi = async (programId: string): Promise<DetailedProgramItem> => {
    return await axios.get(`${BASE_URL}/programs/${programId}`).then((response) => response.data as DetailedProgramItem)
}

export const getAllocationReadinessApi = async (programId: string): Promise<AllocationReadinessResult> => {
    return await axios.get(`${BASE_URL}/programs/${programId}/allocations/readiness`).then((response) => response.data as AllocationReadinessResult)
}

export const commitAllocationSubmissionApi = async (programId: string, submission: AllocationSubmission): Promise<AllocationReadinessResult> => {
    return await axios.post(`${BASE_URL}/programs/${programId}/allocations/commit`, submission).then((response) => response.data as AllocationReadinessResult)
}

export const linkUtilityPortalApi = async (programId: string, portalCredentials: UtilityPortalCredentials): Promise<LinkUtilityPortalResult> => {
    return await axios.post(`${BASE_URL}/programs/${programId}/portal/link`, portalCredentials).then((response) => response.data as LinkUtilityPortalResult)
}

export const updateUtilityPortalCredentialsApi = async (programId: string, portalCredentials: UtilityPortalCredentials): Promise<LinkUtilityPortalResult> => {
    return await axios.put(`${BASE_URL}/programs/${programId}/portal/link`, portalCredentials).then((response) => response.data as LinkUtilityPortalResult)
}

export const getReadinessCheckListApi = async (programId: string): Promise<ReadinessChecklist> => {
    return await axios.get(`${BASE_URL}/programs/${programId}/checklist`).then((response) => response.data as ReadinessChecklist)
}

export const getIntervalDataReadinessCheckListItemApi = async (programId: string): Promise<IntervalDataReadinessChecklistItem> => {
    return await axios.get(`${BASE_URL}/programs/${programId}/checklist/interval-data`).then((response) => response.data as IntervalDataReadinessChecklistItem)
}

export const updateScraperActiveFlags = async (
    programId: string,
    isScraperActive?: boolean,
    isBillProcessingActive?: boolean
): Promise<UpdateScraperIsActiveResult> => {
    return await axios
        .put(`${BASE_URL}/programs/${programId}/portal/scraper/active`, {
            isScraperActive,
            isBillProcessingActive,
        })
        .then((response) => response.data as UpdateScraperIsActiveResult)
}

export const updateProgramApi = async (programId: string, body: UpdateProgramBody): Promise<{ message: string; success: boolean }> => {
    return await axios.put(`${BASE_URL}/programs/${programId}`, body).then((response) => response.data as { message: string; success: boolean })
}

export const createProgramApi = async (body: CreateProgramBody): Promise<CreateProgramResponse> => {
    return await axios.post(`${BASE_URL}/programs`, body).then((response) => response.data as CreateProgramResponse)
}

export const getVNEMAllocationsApi = async (programId: string): Promise<GetVNEMAllocationsResponse> => {
    return await axios.get(`${BASE_URL}/programs/${programId}/vnem-allocations`).then((response) => response.data as GetVNEMAllocationsResponse)
}

export const activateVNEMAllocationApi = async (
    programId: string,
    submissionCuid: string,
    effectiveDate: string
): Promise<ActivateVNEMAllocationResponse> => {
    return await axios
        .put(`${BASE_URL}/programs/${programId}/vnem-allocations/${submissionCuid}/activate`, { effectiveDate })
        .then((response) => response.data as ActivateVNEMAllocationResponse)
}

export const updateVNEMPercentagesApi = async (
    programId: string,
    submissionCuid: string,
    body: UpdateVNEMPercentagesBody
): Promise<UpdateVNEMPercentagesResponse> => {
    return await axios
        .put(`${BASE_URL}/programs/${programId}/vnem-allocations/${submissionCuid}/percentages`, body)
        .then((response) => response.data as UpdateVNEMPercentagesResponse)
}

export const listProgramGeneratingMetersApi = async (programId: string): Promise<ProgramGeneratingMeterRow[]> => {
    return await axios
        .get(`${BASE_URL}/programs/${programId}/generating-meters`)
        .then((response) => response.data as ProgramGeneratingMeterRow[])
}

export const getGeneratingMeterOptionsApi = async (programId: string): Promise<GeneratingMeterOption[]> => {
    return await axios
        .get(`${BASE_URL}/programs/${programId}/generating-meters/options`)
        .then((response) => response.data as GeneratingMeterOption[])
}

export const createProgramGeneratingMeterApi = async (
    programId: string,
    body: CreateProgramGeneratingMeterBody
): Promise<ProgramGeneratingMeterRow> => {
    return await axios
        .post(`${BASE_URL}/programs/${programId}/generating-meters`, body)
        .then((response) => response.data as ProgramGeneratingMeterRow)
}

export const updateProgramGeneratingMeterApi = async (
    programId: string,
    linkCuid: string,
    body: UpdateProgramGeneratingMeterBody
): Promise<ProgramGeneratingMeterRow> => {
    return await axios
        .put(`${BASE_URL}/programs/${programId}/generating-meters/${linkCuid}`, body)
        .then((response) => response.data as ProgramGeneratingMeterRow)
}

export const retireProgramGeneratingMeterApi = async (
    programId: string,
    linkCuid: string,
    body: RetireProgramGeneratingMeterBody
): Promise<ProgramGeneratingMeterRow> => {
    return await axios
        .post(`${BASE_URL}/programs/${programId}/generating-meters/${linkCuid}/retire`, body)
        .then((response) => response.data as ProgramGeneratingMeterRow)
}

export const listProgramMetersApi = async (programId: string): Promise<ProgramMeterRow[]> => {
    return await axios
        .get(`${BASE_URL}/programs/${programId}/meters`)
        .then((response) => response.data as ProgramMeterRow[])
}

export const getProgramMeterOptionsApi = async (programId: string): Promise<ProgramMeterOption[]> => {
    return await axios
        .get(`${BASE_URL}/programs/${programId}/meters/options`)
        .then((response) => response.data as ProgramMeterOption[])
}

export const createProgramMeterApi = async (
    programId: string,
    body: CreateProgramMeterBody
): Promise<ProgramMeterRow> => {
    return await axios
        .post(`${BASE_URL}/programs/${programId}/meters`, body)
        .then((response) => response.data as ProgramMeterRow)
}

export const updateProgramMeterApi = async (
    programId: string,
    programMeterCuid: string,
    body: UpdateProgramMeterBody
): Promise<ProgramMeterRow> => {
    return await axios
        .put(`${BASE_URL}/programs/${programId}/meters/${programMeterCuid}`, body)
        .then((response) => response.data as ProgramMeterRow)
}

export const retireProgramMeterApi = async (
    programId: string,
    programMeterCuid: string,
    body: RetireProgramMeterBody
): Promise<ProgramMeterRow> => {
    return await axios
        .post(`${BASE_URL}/programs/${programId}/meters/${programMeterCuid}/retire`, body)
        .then((response) => response.data as ProgramMeterRow)
}

export const listPropertyUtilitiesApi = async (propertyId: string): Promise<PropertyUtilityRow[]> => {
    return await axios
        .get(`${BASE_URL}/property/${propertyId}/utilities`)
        .then((response) => response.data as PropertyUtilityRow[])
}

export const getPropertyUtilityOptionsApi = async (propertyId: string): Promise<UtilityOption[]> => {
    return await axios
        .get(`${BASE_URL}/property/${propertyId}/utilities/options`)
        .then((response) => response.data as UtilityOption[])
}

export const linkPropertyUtilityApi = async (
    propertyId: string,
    body: LinkPropertyUtilityBody
): Promise<PropertyUtilityRow> => {
    return await axios
        .post(`${BASE_URL}/property/${propertyId}/utilities`, body)
        .then((response) => response.data as PropertyUtilityRow)
}

export const unlinkPropertyUtilityApi = async (propertyId: string, propertyUtilityCuid: string): Promise<void> => {
    await axios.delete(`${BASE_URL}/property/${propertyId}/utilities/${propertyUtilityCuid}`)
}

export const getEnergyTaxCatalogApi = async (params?: {
    region?: string
    effectiveOn?: string
}): Promise<EnergyTaxCatalogItem[]> => {
    return await axios
        .get(`${BASE_URL}/energy-taxes`, { params })
        .then((response) => response.data as EnergyTaxCatalogItem[])
}

/** Creates a catalog row only; it is not linked to any property or utility. */
export const createEnergyTaxApi = async (body: CreateEnergyTaxBody): Promise<EnergyTaxCatalogItem> => {
    return await axios
        .post(`${BASE_URL}/energy-taxes`, body)
        .then((response) => response.data as EnergyTaxCatalogItem)
}

export const listPropertyUtilityTaxesApi = async (
    propertyId: string,
    propertyUtilityCuid: string
): Promise<PropertyUtilityTaxRow[]> => {
    return await axios
        .get(`${BASE_URL}/property/${propertyId}/utilities/${propertyUtilityCuid}/taxes`)
        .then((response) => response.data as PropertyUtilityTaxRow[])
}

export const syncPropertyUtilityTaxesApi = async (
    propertyId: string,
    propertyUtilityCuid: string,
    body: SyncPropertyUtilityTaxesBody
): Promise<SyncPropertyUtilityTaxesResult> => {
    return await axios
        .put(`${BASE_URL}/property/${propertyId}/utilities/${propertyUtilityCuid}/taxes`, body)
        .then((response) => response.data as SyncPropertyUtilityTaxesResult)
}

export const enrollSmdApi = async (programId: string): Promise<SmdJobResponse> => {
    return await axios.post(`${BASE_URL}/programs/${programId}/smd/enroll`).then((response) => response.data as SmdJobResponse)
}

export const cancelSmdApi = async (programId: string): Promise<SmdJobResponse> => {
    return await axios.post(`${BASE_URL}/programs/${programId}/smd/cancel`).then((response) => response.data as SmdJobResponse)
}

export const getBillingOptionsApi = async (): Promise<BillingOptionsResponse> => {
    return await axios.get(`${BASE_URL}/tenant-account/billing-options`).then((res) => res.data as BillingOptionsResponse)
}

export const updateUtilityBillingConfigApi = async (
    body: UpdateUtilityBillingConfigBody,
    tenantAccountId: string
): Promise<SharedTenantAccountUpdateResponse> => {
    return await axios
        .put(`${BASE_URL}/tenant-account/${tenantAccountId}/billing-config/utility`, body)
        .then((res) => res.data as SharedTenantAccountUpdateResponse)
}

export const updateKingBillingConfigApi = async (body: UpdateKingBillingConfigBody, tenantAccountId: string): Promise<SharedTenantAccountUpdateResponse> => {
    return await axios.put(`${BASE_URL}/tenant-account/${tenantAccountId}/billing-config/king`, body).then((res) => res.data as SharedTenantAccountUpdateResponse)
}

export const updateBillingModeApi = async (
    billingMode: string,
    tenantAccountId: string,
    utilityBalanceAdjustmentAmount?: string,
    invoiceBalanceAdjustmentAmount?: string
): Promise<SharedTenantAccountUpdateResponse> => {
    const payload: {
        billingMode: string;
        utilityBalanceAdjustmentAmount?: number;
        invoiceBalanceAdjustmentAmount?: number;
    } = { billingMode };

    if (utilityBalanceAdjustmentAmount) {
        payload.utilityBalanceAdjustmentAmount = parseFloat(utilityBalanceAdjustmentAmount);
    }

    if (invoiceBalanceAdjustmentAmount) {
        payload.invoiceBalanceAdjustmentAmount = parseFloat(invoiceBalanceAdjustmentAmount);
    }

    return await axios.put(`${BASE_URL}/tenant-account/${tenantAccountId}/billing-mode`, payload).then((res) => res.data as SharedTenantAccountUpdateResponse)
}

export const createUtilityIncentivePlanApi = async (
    body: CreateUtilityIncentivePlanBody,
    tenantAccountId: string
): Promise<SharedTenantAccountUpdateResponse> => {
    return await axios
        .post(`${BASE_URL}/tenant-account/${tenantAccountId}/incentive-plan/utility`, body)
        .then((res) => res.data as SharedTenantAccountUpdateResponse)
}

export const createKingIncentivePlanApi = async (body: CreateKingIncentivePlanBody, tenantAccountId: string): Promise<SharedTenantAccountUpdateResponse> => {
    return await axios
        .post(`${BASE_URL}/tenant-account/${tenantAccountId}/incentive-plan/king`, body)
        .then((res) => res.data as SharedTenantAccountUpdateResponse)
}

export const getIncentivePlanOptionsApi = async (): Promise<IncentivePlanOptionsResponse> => {
    return await axios.get(`${BASE_URL}/tenant-account/incentive-plan-options`).then((res) => res.data as IncentivePlanOptionsResponse)
}

export const cancelKingIncentivePlanApi = async (
    tenantAccountId: string,
    planId: string,
    reason: string
): Promise<SharedTenantAccountUpdateResponse> => {
    return await axios
        .patch(`${BASE_URL}/tenant-account/${tenantAccountId}/incentive-plan/${planId}/cancel`, { reason })
        .then((res) => res.data as SharedTenantAccountUpdateResponse)
}

export const cancelAccommodationCreditApi = async (
    tenantAccountId: string,
    creditId: string,
    reason: string
): Promise<SharedTenantAccountUpdateResponse> => {
    return await axios
        .patch(`${BASE_URL}/tenant-account/${tenantAccountId}/accommodation-credit/${creditId}/cancel`, { reason })
        .then((res) => res.data as SharedTenantAccountUpdateResponse)
}

export const reverseAccommodationCreditApi = async (
    tenantAccountId: string,
    creditId: string,
    reason: string,
    installmentIds: string[]
): Promise<SharedTenantAccountUpdateResponse> => {
    return await axios
        .patch(`${BASE_URL}/tenant-account/${tenantAccountId}/accommodation-credit/${creditId}/reverse`, { reason, installmentIds })
        .then((res) => res.data as SharedTenantAccountUpdateResponse)
}

export const getIncentivePlanAuditHistoryApi = async (
    tenantAccountId: string,
    planCuid: string
): Promise<IncentivePlanAuditEntry[]> => {
    return await axios
        .get(`${BASE_URL}/tenant-account/${tenantAccountId}/incentive-plan/${planCuid}/audit-history`)
        .then((res) => res.data as IncentivePlanAuditEntry[])
}

export const enrollInOptirateApi = async (meterServiceCuid: string, requestDate: string, targetTariffCuid: string, creditPayee?: CreditPayee): Promise<SharedTenantAccountUpdateResponse> => {
    return await axios.post(`${BASE_URL}/tenant-account/${meterServiceCuid}/optirate/enroll`, { requestDate, targetTariffCuid, creditPayee }).then((res) => res.data as SharedTenantAccountUpdateResponse)
}

export const updateOptiRateCreditPayeeApi = async (meterServiceCuid: string, creditPayee: CreditPayee): Promise<SharedTenantAccountUpdateResponse> => {
    return await axios.put(`${BASE_URL}/tenant-account/${meterServiceCuid}/optirate/credit-payee`, { creditPayee }).then((res) => res.data as SharedTenantAccountUpdateResponse)
}

export const getTargetTariffsApi = async (meterServiceCuid: string, utility: UtilityType): Promise<OptiRateTargetTariff[]> => {
    return await axios.get(`${BASE_URL}/tenant-account/${meterServiceCuid}/optirate/target-tariffs`, { params: { utility } }).then((res) => res.data as OptiRateTargetTariff[])
}

export const createAgreementFromUploadApi = async (invitationCuid: string, formData: FormData): Promise<unknown> => {
    return await axios.post(`${BASE_URL}/tesa/feed/agreement/create-from-upload/${invitationCuid}`, formData).then((res) => res.data as unknown)
}

export const updateAgreementFromUploadApi = async (agreementCuid: string, formData: FormData): Promise<unknown> => {
    return await axios.put(`${BASE_URL}/tesa/feed/agreement/${agreementCuid}/submission/update-from-upload`, formData).then((res) => res.data as unknown)
}

export const getTenantAccountBalancesFromBillingSdkApi = async (tenantAccountId: string): Promise<BillingSdkTenantAccountBalances> => {
    return await axios.get(`${BASE_URL}/tenant-account/${tenantAccountId}/billing-event-sdk-balances`).then((res) => res.data as BillingSdkTenantAccountBalances)
}

export const getBillingEventsApi = async (tenantAccountId: string): Promise<TenantAccountBillingEventEntry[]> => {
    return await axios.get(`${BASE_URL}/collections/billing-event/${tenantAccountId}`).then((res) => res.data as TenantAccountBillingEventEntry[])
}

export const getBillingEventEnumsApi = async (): Promise<BillingEventEnums> => {
    return await axios.get(`${BASE_URL}/collections/billing-event/enums`).then((res) => res.data as BillingEventEnums)
}

export const reclassBillingEventApi = async (
    tenantAccountId: string,
    eventId: string,
    newEventType: string,
    transactionDate?: string
): Promise<{ success: boolean; message: string }> => {
    const requestBody: { newEventType: string; transactionDate?: string } = { newEventType }
    if (transactionDate) {
        requestBody.transactionDate = transactionDate
    }
    return await axios
        .put(`${BASE_URL}/collections/billing-event/${tenantAccountId}/${eventId}/reclass`, requestBody)
        .then((res) => res.data as { success: boolean; message: string })
}

export const reversePaymentApi = async (
    tenantAccountId: string,
    eventId: string,
    transactionDate?: string
): Promise<{ success: boolean; message: string }> => {
    const requestBody: { transactionDate?: string } = {}
    if (transactionDate) {
        requestBody.transactionDate = transactionDate
    }
    return await axios
        .put(`${BASE_URL}/collections/billing-event/${tenantAccountId}/${eventId}/reverse`, requestBody)
        .then((res) => res.data as { success: boolean; message: string })
}

export const editBillingEventApi = async (tenantAccountId: string, eventId: string, editData: EditBillingEventRequest): Promise<{ success: boolean; message: string }> => {
    try {
        console.log('Edit billing event API call:', { tenantAccountId, eventId, editData });
        const response = await axios.put(`${BASE_URL}/collections/billing-event/${tenantAccountId}/${eventId}/edit`, editData);
        console.log('Edit billing event API response:', response.data);
        return response.data as { success: boolean; message: string };
    } catch (error: any) {
        console.error('Edit billing event API error:', error.response?.data || error.message);
        throw error;
    }
}

export const getReversalStatusApi = async (tenantAccountId: string): Promise<{ [eventId: string]: boolean }> => {
    return await axios.get(`${BASE_URL}/collections/billing-event/${tenantAccountId}/reversal-status`).then((res) => res.data as { [eventId: string]: boolean })
}

export const reverseInvoiceAccruedApi = async (
    tenantAccountId: string,
    eventId: string,
    payload: ReverseInvoiceAccruedRequest
): Promise<{ success: boolean; message: string }> => {
    return await axios
        .put(`${BASE_URL}/collections/billing-event/${tenantAccountId}/${eventId}/reverse-invoice`, payload)
        .then((res) => res.data as { success: boolean; message: string })
}

export const getBillingEventAuditLogApi = async (tenantAccountId: string): Promise<BillingEventAuditLogEntry[]> => {
    return await axios.get(`${BASE_URL}/collections/billing-event/${tenantAccountId}/audit-log`).then((res) => res.data as BillingEventAuditLogEntry[])
}

export const exportBillingEventsCsvApi = async (tenantAccountId: string): Promise<Blob> => {
    const response = await axios.get(`${BASE_URL}/collections/billing-event/${tenantAccountId}/export-csv`, {
        responseType: 'blob'
    })
    return response.data
}

export const updateAuditLogStatusApi = async (
    tenantAccountId: string,
    auditLogCuid: string,
    payload: UpdateAuditLogStatusRequest
): Promise<{ success: boolean; message: string }> => {
    return await axios
        .patch(`${BASE_URL}/collections/billing-event/${tenantAccountId}/audit-log/${auditLogCuid}/status`, payload)
        .then((res) => res.data as { success: boolean; message: string })
}

export const updateAuditLogEventBodyApi = async (
    tenantAccountId: string,
    auditLogCuid: string,
    payload: UpdateAuditLogEventBodyRequest
): Promise<{ success: boolean; message: string }> => {
    return await axios
        .patch(`${BASE_URL}/collections/billing-event/${tenantAccountId}/audit-log/${auditLogCuid}/event-body`, payload)
        .then((res) => res.data as { success: boolean; message: string })
}

export const updateAdminUserLastLoginApi = (payload: { emailAddress: string }) => {
    return axios
        .put(`${BASE_URL}/users/lastLogin`, payload)
        .then((res) => res.data)
}

export interface DocumentTypeInfo {
    type: string
    label: string
    uploadable: boolean
    replaceable: boolean
}

export const getDocumentTypesApi = async (): Promise<DocumentTypeInfo[]> => {
    return await axios.get(`${BASE_URL}/tenant-account/documents/types`).then((res) => res.data as DocumentTypeInfo[])
}

export const getDocumentsApi = async (tenantAccountId: string, query?: GetDocumentsArgs): Promise<GetDocumentsData> => {
    return await axios.get(`${BASE_URL}/tenant-account/documents/${tenantAccountId}`, { params: query }).then((res) => res.data as GetDocumentsData)
}

export const downloadDocumentApi = async (tenantAccountId: string, documentId: string): Promise<{ data: ArrayBuffer; filename: string }> => {
    const response = await axios.get(`${BASE_URL}/tenant-account/documents/${tenantAccountId}/download/${documentId}`, {
        responseType: 'arraybuffer'
    })

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers['content-disposition']
    const filenameMatch = contentDisposition?.match(/filename="([^"]+)"/)
    const filename = filenameMatch ? filenameMatch[1] : `document_${documentId}.pdf`

    return {
        data: response.data,
        filename
    }
}

export const uploadDocumentApi = async (
    tenantAccountId: string,
    file: File,
    documentType: string,
    signal?: AbortSignal
): Promise<DocumentMetadata> => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('documentType', documentType)

    return await axios
        .post(`${BASE_URL}/tenant-account/documents/${tenantAccountId}/upload`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            signal,
        })
        .then((res) => res.data as DocumentMetadata)
}

export const replaceDocumentApi = async (
    tenantAccountId: string,
    documentId: string,
    file: File,
    signal?: AbortSignal
): Promise<DocumentMetadata> => {
    const formData = new FormData()
    formData.append('file', file)

    return await axios
        .post(
            `${BASE_URL}/tenant-account/documents/${tenantAccountId}/upload/${documentId}/replace`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' }, signal }
        )
        .then((res) => res.data as DocumentMetadata)
}

export const getTenantAccountBillingStatusApi = async (tenantAccountId: string): Promise<TenantAccountBillingStatusResponse> => {
    return await axios.get(`${BASE_URL}/tenant-account/${tenantAccountId}/billing-status`).then((res) => res.data as TenantAccountBillingStatusResponse)
}

export const getCcFeePolicyApi = async (tenantAccountId: string): Promise<CcFeePolicyAdmin> => {
    return await axios
        .get(`${BASE_URL}/tenant-account/${tenantAccountId}/cc-fee-policy`)
        .then((res) => res.data as CcFeePolicyAdmin)
}

export const updateTenantAccountBillingStatusApi = async (tenantAccountId: string, data: any): Promise<any> => {
    return await axios.put(`${BASE_URL}/tenant-account/${tenantAccountId}/billing-status`, data).then((res) => res.data)
}

export const getTenantAccountStatusApi = async (tenantAccountId: string): Promise<TenantAccountStatusResponse> => {
    return await axios.get(`${BASE_URL}/tenant-account/${tenantAccountId}/status`).then((res) => res.data as TenantAccountStatusResponse)
}

export const updateTenantAccountStatusApi = async (
  tenantAccountId: string,
  data: {
    status: string
    cancellationReason?: string
    cancellationNoticeDays?: number
    modifiedDescription?: string
  }
): Promise<{ success: boolean }> => {
    return await axios.put(`${BASE_URL}/tenant-account/${tenantAccountId}/status`, data).then((res) => res.data)
}

// Base Case Model APIs
export interface FuzzyMatchSuggestion {
    cuid: string
    name: string
    distance: number
}

export interface FuzzyMatch {
    rowNumber: number
    field: 'project' | 'property' | 'investor'
    inputValue: string
    suggestions: FuzzyMatchSuggestion[]
}

export interface BaseCaseModelUploadResponse {
    success: boolean
    recordsProcessed: number
    recordsCreated: number
    recordsSkipped: number
    duplicates: string[]
    errors?: string[]
    fuzzyMatches?: FuzzyMatch[]
}

export const uploadBaseCaseModelCsv = async (
    file: File,
    shouldPersist: boolean,
    nameResolutions?: Record<string, string>
): Promise<BaseCaseModelUploadResponse> => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('shouldPersist', shouldPersist.toString())
    if (nameResolutions) {
        formData.append('nameResolutions', JSON.stringify(nameResolutions))
    }

    return await axios
        .post(`${BASE_URL}/base-case-model/upload`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        })
        .then((res) => res.data as BaseCaseModelUploadResponse)
}

// Event Handling Report APIs
export const getEventReportApi = async (query: GetEventReportArgs): Promise<Paginated<EventReportEntry>> => {
    return await axios.get(`${BASE_URL}/events/report`, { params: query }).then((res) => res.data as Paginated<EventReportEntry>)
}

export const getEventSummaryApi = async (query: GetEventReportArgs): Promise<EventSummary> => {
    return await axios.get(`${BASE_URL}/events/summary`, { params: query }).then((res) => res.data as EventSummary)
}

export const getEventTypesApi = async (query: GetEventReportArgs): Promise<string[]> => {
    return await axios.get(`${BASE_URL}/events/types`, { params: query }).then((res) => res.data as string[])
}

// Ticket APIs
export const getTicketsForTenantApi = async (tenantCuid: string, query?: TicketSearchParams): Promise<Paginated<TenantTicket>> => {
    return await axios.get(`${BASE_URL}/tenant-account/tickets/by-tenant/${tenantCuid}`, { params: query }).then((res) => res.data as Paginated<TenantTicket>)
}

export const getTicketEnumsApi = async (): Promise<TicketEnums> => {
    return await axios.get(`${BASE_URL}/tenant-account/tickets/enums`).then((res) => res.data as TicketEnums)
}

// Reconciliation Report APIs
export const getReconciliationReportApi = async (
    programId: string,
    query?: ReconciliationQueryParams
): Promise<ReconciliationReportVO> => {
    return await axios
        .get(`${BASE_URL}/programs/${programId}/reconciliation`, { params: query })
        .then((res) => res.data as ReconciliationReportVO)
}

// Go-Live Readiness (fleet-wide Stage A gate rollup). WEB-6378.
export const getGoLiveReadinessApi = async (): Promise<GoLiveReadinessVO> => {
    return await axios
        .get(`${BASE_URL}/reconciliation/readiness`)
        .then((res) => res.data as GoLiveReadinessVO)
}

// Tariff APIs
export const getTariffsApi = async (query: GetTariffsArgs): Promise<GetTariffsListData> => {
    return await axios.get(`${BASE_URL}/tariff`, { params: query }).then((res) => res.data as GetTariffsListData)
}

export const createTariffApi = async (body: CreateTariffBody): Promise<DetailedTariffItem> => {
    return await axios.post(`${BASE_URL}/tariff`, body).then((res) => res.data as DetailedTariffItem)
}

export const deleteTariffApi = async (tariffId: string, reason: string): Promise<void> => {
    await axios.delete(`${BASE_URL}/tariff/${tariffId}`, { params: { reason } })
}

export const getTariffHistoriesApi = async (tariffId: string): Promise<TariffHistoryItem[]> => {
    return await axios
        .get(`${BASE_URL}/tariff/${tariffId}/history`)
        .then((res) => res.data as TariffHistoryItem[])
}

export const getTariffHistoryApi = async (
    tariffId: string,
    historyCuid: string
): Promise<TariffHistoryItem> => {
    return await axios
        .get(`${BASE_URL}/tariff/${tariffId}/history/${historyCuid}`)
        .then((res) => res.data as TariffHistoryItem)
}

export const updateTariffHistoryApi = async (
    historyCuid: string,
    body: {
        endDate?: string | null
        notes?: string | null
        modifiedDescription?: string
    }
): Promise<DetailedTariffItem> => {
    return await axios
        .patch(`${BASE_URL}/tariff/history/${historyCuid}`, body)
        .then((res) => res.data as DetailedTariffItem)
}

export const createTariffHistoryApi = async (
    tariffId: string,
    body: CreateTariffHistoryBody
): Promise<DetailedTariffItem> => {
    return await axios
        .post(`${BASE_URL}/tariff/${tariffId}/history`, body)
        .then((res) => res.data as DetailedTariffItem)
}

export const getTariffActivatePreviewApi = async (
    historyCuid: string
): Promise<TariffActivatePreview> => {
    return await axios
        .get(`${BASE_URL}/tariff/history/${historyCuid}/activate-preview`)
        .then((res) => res.data as TariffActivatePreview)
}

export const activateTariffHistoryApi = async (
    historyCuid: string,
    body: TariffHistoryReasonBody
): Promise<DetailedTariffItem> => {
    return await axios
        .post(`${BASE_URL}/tariff/history/${historyCuid}/activate`, body)
        .then((res) => res.data as DetailedTariffItem)
}

export const withdrawTariffHistoryApi = async (
    historyCuid: string,
    body: TariffHistoryReasonBody
): Promise<DetailedTariffItem> => {
    return await axios
        .post(`${BASE_URL}/tariff/history/${historyCuid}/withdraw`, body)
        .then((res) => res.data as DetailedTariffItem)
}

export const retireTariffHistoryApi = async (
    historyCuid: string,
    body: TariffHistoryReasonBody
): Promise<DetailedTariffItem> => {
    return await axios
        .post(`${BASE_URL}/tariff/history/${historyCuid}/retire`, body)
        .then((res) => res.data as DetailedTariffItem)
}

export const createTariffRateApi = async (
    tariffId: string,
    body: CreateTariffRateBody
): Promise<TariffRateItem> => {
    return await axios
        .post(`${BASE_URL}/tariff/${tariffId}/rates`, body)
        .then((res) => res.data as TariffRateItem)
}

export const getTariffApi = async (
    tariffId: string,
    query?: { historyCuid?: string }
): Promise<DetailedTariffItem> => {
    return await axios
        .get(`${BASE_URL}/tariff/${tariffId}`, { params: query })
        .then((res) => res.data as DetailedTariffItem)
}

export const updateTariffApi = async (tariffId: string, body: UpdateTariffBody): Promise<DetailedTariffItem> => {
    return await axios.patch(`${BASE_URL}/tariff/${tariffId}`, body).then((res) => res.data as DetailedTariffItem)
}

export const updateTariffRateApi = async (
    rateCuid: string,
    body: UpdateTariffRateBody
): Promise<TariffRateItem> => {
    return await axios
        .patch(`${BASE_URL}/tariff/rate/${rateCuid}`, body)
        .then((res) => res.data as TariffRateItem)
}

export const deleteTariffRateApi = async (rateCuid: string, reason: string): Promise<void> => {
    await axios.delete(`${BASE_URL}/tariff/rate/${rateCuid}`, { params: { reason } })
}

export const getTariffUsagesApi = async (
    tariffId: string,
    query?: { page?: number; resultsPerPage?: number; activeOnly?: boolean }
): Promise<GetTariffUsagesData> => {
    const params =
        query == null
            ? undefined
            : {
                  ...query,
                  ...(query.activeOnly != null ? { activeOnly: String(query.activeOnly) } : {}),
              }
    return await axios
        .get(`${BASE_URL}/tariff/${tariffId}/usages`, { params })
        .then((res) => res.data as GetTariffUsagesData)
}

export const uploadTariffHistoryAttachmentApi = async (
    historyCuid: string,
    args: {
        file: File
        label: string
        category: TariffRateSheetCategory
        documentDate?: string
        vintageYear?: string | null
        isShared?: boolean
    },
    signal?: AbortSignal
): Promise<TariffHistoryAttachment> => {
    const formData = new FormData()
    formData.append('file', args.file)
    formData.append('label', args.label)
    formData.append('category', args.category)
    if (args.documentDate != null && args.documentDate !== '') {
        formData.append('documentDate', args.documentDate)
    }
    if (args.vintageYear != null && args.vintageYear !== '') {
        formData.append('vintageYear', args.vintageYear)
    }
    if (args.isShared === true) {
        formData.append('isShared', 'true')
    }

    return await axios
        .post(`${BASE_URL}/tariff/history/${historyCuid}/attachments`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            signal,
        })
        .then((res) => res.data as TariffHistoryAttachment)
}

export const listUtilityRateSheetsApi = async (
    utilityCuid: string,
    query?: { sharedOnly?: boolean; category?: TariffRateSheetCategory; search?: string }
): Promise<TariffHistoryAttachment[]> => {
    return await axios
        .get(`${BASE_URL}/tariff/utility/${utilityCuid}/rate-sheets`, { params: query })
        .then((res) => res.data as TariffHistoryAttachment[])
}

export const linkRateSheetToHistoryApi = async (
    historyCuid: string,
    rateSheetCuid: string
): Promise<TariffHistoryAttachment> => {
    return await axios
        .post(`${BASE_URL}/tariff/history/${historyCuid}/rate-sheets/${rateSheetCuid}`)
        .then((res) => res.data as TariffHistoryAttachment)
}

export const unlinkRateSheetFromHistoryApi = async (
    historyCuid: string,
    rateSheetCuid: string
): Promise<void> => {
    await axios.delete(`${BASE_URL}/tariff/history/${historyCuid}/rate-sheets/${rateSheetCuid}`)
}

export const updateRateSheetApi = async (
    rateSheetCuid: string,
    body: {
        isShared?: boolean
        label?: string
        category?: TariffRateSheetCategory
        vintageYear?: string | null
        documentDate?: string
        reason?: string
    }
): Promise<TariffHistoryAttachment> => {
    return await axios
        .patch(`${BASE_URL}/tariff/rate-sheet/${rateSheetCuid}`, body)
        .then((res) => res.data as TariffHistoryAttachment)
}

export const deleteRateSheetApi = async (rateSheetCuid: string): Promise<void> => {
    await axios.delete(`${BASE_URL}/tariff/rate-sheet/${rateSheetCuid}`)
}

export const downloadTariffAttachmentApi = async (
    attachmentCuid: string,
    disposition: 'inline' | 'attachment' = 'attachment',
    signal?: AbortSignal
): Promise<{ data: ArrayBuffer; filename: string; contentType: string | null }> => {
    const path = disposition === 'inline' ? 'view' : 'download'
    const response = await axios.get(`${BASE_URL}/tariff/attachment/${attachmentCuid}/${path}`, {
        responseType: 'arraybuffer',
        signal,
    })

    const contentDisposition = response.headers['content-disposition'] as string | undefined
    const filenameMatch = contentDisposition?.match(/filename="([^"]+)"/)
    const filename = filenameMatch?.[1] ?? `rate-sheet_${attachmentCuid}`
    const contentType = (response.headers['content-type'] as string | undefined) ?? null

    return {
        data: response.data,
        filename,
        contentType,
    }
}

export const getTariffAuditHistoryApi = async (
    tariffId: string,
    query?: { page?: number; resultsPerPage?: number; entityType?: TariffAuditEntityType }
): Promise<GetTariffAuditHistoryData> => {
    return await axios
        .get(`${BASE_URL}/tariff/${tariffId}/audit-history`, { params: query })
        .then((res) => res.data as GetTariffAuditHistoryData)
}

export const getTariffRateAnalysisApi = async (tariffId: string): Promise<TariffRateAnalysisRow[]> => {
    return await axios
        .get(`${BASE_URL}/tariff/${tariffId}/rate-analysis`)
        .then((res) => res.data as TariffRateAnalysisRow[])
}

export const getUtilityRateAnalysisApi = async (
    utilityCuid: string,
    schedule?: string
): Promise<TariffRateAnalysisRow[]> => {
    return await axios
        .get(`${BASE_URL}/tariff/rate-analysis`, { params: { utilityCuid, schedule } })
        .then((res) => res.data as TariffRateAnalysisRow[])
}

export const exportTariffApi = async (tariffId: string, format: 'xlsx' | 'csv'): Promise<Blob> => {
    const response = await axios.get(`${BASE_URL}/tariff/${tariffId}/export`, {
        params: { format },
        responseType: 'blob',
    })
    return response.data as Blob
}

export const getAccRateVintagesApi = async (utilityCuid: string): Promise<AccRateVintage[]> => {
    return await axios
        .get(`${BASE_URL}/tariff/utility/${utilityCuid}/acc-rates/vintages`)
        .then((res) => res.data as AccRateVintage[])
}

export const getAccRateGridApi = async (
    utilityCuid: string,
    year?: number
): Promise<AccRateGridRow[]> => {
    return await axios
        .get(`${BASE_URL}/tariff/utility/${utilityCuid}/acc-rates`, {
            params: year != null ? { year } : undefined,
        })
        .then((res) => res.data as AccRateGridRow[])
}

export const exportAccRatesCsvApi = async (utilityCuid: string, year?: number): Promise<Blob> => {
    const response = await axios.get(`${BASE_URL}/tariff/utility/${utilityCuid}/acc-rates/export-csv`, {
        params: year != null ? { year } : undefined,
        responseType: 'blob',
    })
    return response.data as Blob
}

export const createUtilityApi = async (body: CreateUtilityBody): Promise<Utility> => {
    return await axios.post(`${BASE_URL}/utility`, body).then((res) => res.data as Utility)
}

export const updateUtilityApi = async (cuid: string, body: UpdateUtilityBody): Promise<Utility> => {
    return await axios.patch(`${BASE_URL}/utility/${cuid}`, body).then((res) => res.data as Utility)
}
